import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { TrialWhatsAppDialog } from './TrialWhatsAppDialog';
import { toast } from 'sonner';
import api from '../../lib/api';
import { 
  Users, Mail, Clock, CheckCircle, Send, Calendar, 
  Phone, User, Baby, AlertCircle, MailCheck, MailX,
  UserCheck, UserX, Filter, X, ChevronRight, Loader2, Trash2, Edit, Link2,
  Eye, UserPlus, Copy as CopyIcon, MessageCircle, Undo2, Search
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import ReferralInsightsCard from './ReferralInsightsCard';

const TrialsTab = ({
  trials,
  trialsLoading,
  sendingTrialEmail,
  fetchTrials,
  handleSendTrialReminder,
  handleSendTrialFollowup
}) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [updatingAttendance, setUpdatingAttendance] = useState(null);
  const [deletingTrial, setDeletingTrial] = useState(null);
  const [editingTrial, setEditingTrial] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [savingDate, setSavingDate] = useState(false);
  const [sendingDateLink, setSendingDateLink] = useState(null);
  const [detailsTrial, setDetailsTrial] = useState(null);
  const [selectedTrialIds, setSelectedTrialIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [deletedTrials, setDeletedTrials] = useState([]);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [restoringTrial, setRestoringTrial] = useState(null);
  const [waTrial, setWaTrial] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { setSelectedTrialIds(new Set()); }, [activeFilter]);

  const fetchDeletedTrials = async () => {
    setDeletedLoading(true);
    try {
      const res = await api.get('/free-trials?deleted=true');
      setDeletedTrials(res.data || []);
    } catch (error) {
      console.error('Failed to fetch deleted trials:', error);
    } finally {
      setDeletedLoading(false);
    }
  };

  useEffect(() => { if (showDeleted) fetchDeletedTrials(); }, [showDeleted]);

  const handleRestoreTrial = async (trialId, name) => {
    setRestoringTrial(trialId);
    try {
      await api.post(`/free-trials/${trialId}/restore`);
      toast.success(`${name} restored to the trials list`);
      fetchDeletedTrials();
      fetchTrials();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore trial');
    } finally {
      setRestoringTrial(null);
    }
  };

  const handleImportFromTrial = (trial) => {
    // Navigate to Add Students page and prefill the form from this trial's data
    navigate('/admin/students/import', {
      state: {
        prefillFromTrial: {
          student_name: `${trial.student_first_name || ''} ${trial.student_middle_name || ''} ${trial.student_last_name || ''}`.replace(/\s+/g, ' ').trim(),
          birth_date: trial.student_dob || '',
          gender: (trial.student_gender || '').toLowerCase(),
          medical_conditions: trial.medical_conditions || '',
          parent_email: trial.parent_email || '',
          parent_first_name: trial.parent_first_name || (trial.parent_name || '').split(' ')[0] || '',
          parent_last_name: trial.parent_last_name || (trial.parent_name || '').split(' ').slice(1).join(' ') || '',
          parent_phone: trial.parent_phone || '',
          guardian2_first_name: trial.guardian2?.first_name || '',
          guardian2_last_name: trial.guardian2?.last_name || '',
          guardian2_email: trial.guardian2?.email || '',
          guardian2_phone: trial.guardian2?.phone || '',
          class_id: trial.class_id || '',
          class_interest: trial.class_interest || '',
          trial_id: trial.id,
        },
      },
    });
  };

  const handleCopyTrialDetails = async (trial) => {
    const lines = [
      `Student: ${trial.student_first_name || ''} ${trial.student_middle_name || ''} ${trial.student_last_name || ''}`.replace(/\s+/g, ' ').trim(),
      `DOB: ${trial.student_dob || 'n/a'}`,
      `Gender: ${trial.student_gender || 'n/a'}`,
      `Medical: ${trial.medical_conditions || 'None'}`,
      '',
      `Parent: ${trial.parent_name || ''}`,
      `Email: ${trial.parent_email || ''}`,
      `Phone: ${trial.parent_phone || ''}`,
    ];
    if (trial.guardian2 && trial.guardian2.first_name) {
      lines.push('', `Guardian 2: ${trial.guardian2.first_name} ${trial.guardian2.last_name || ''}`);
      if (trial.guardian2.email) lines.push(`Guardian 2 Email: ${trial.guardian2.email}`);
      if (trial.guardian2.phone) lines.push(`Guardian 2 Phone: ${trial.guardian2.phone}`);
    }
    if (trial.address && trial.address.street) {
      lines.push('', 'Address:',
        trial.address.street,
        trial.address.street2 || '',
        `${trial.address.city || ''} ${trial.address.postcode || ''}`.trim(),
        trial.address.country || '');
    }
    lines.push('', `Class interest: ${trial.class_interest || 'n/a'}`);
    const text = lines.filter(Boolean).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Trial details copied to clipboard');
    } catch {
      toast.error('Could not copy — browser blocked clipboard access');
    }
  };

  // Fetch available trial dates
  useEffect(() => {
    const fetchDates = async () => {
      try {
        const response = await api.get('/academy-info/trial-dates');
        setAvailableDates(response.data || []);
      } catch (error) {
        console.error('Failed to fetch trial dates:', error);
      }
    };
    fetchDates();
  }, []);

  const handleEditTrialDate = (trial) => {
    setEditingTrial(trial);
    setSelectedDate(trial.trial_date || '');
  };

  const handleSaveTrialDate = async () => {
    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }
    
    setSavingDate(true);
    try {
      await api.patch(`/free-trials/${editingTrial.id}/update-date`, { 
        trial_date: selectedDate 
      });
      toast.success('Trial date updated successfully');
      setEditingTrial(null);
      setSelectedDate('');
      fetchTrials();
    } catch (error) {
      console.error('Failed to update trial date:', error);
      toast.error('Failed to update trial date');
    } finally {
      setSavingDate(false);
    }
  };

  const handleSendDateSelectionLink = async (trial) => {
    setSendingDateLink(trial.id);
    try {
      await api.post(`/free-trials/${trial.id}/send-date-selection`);
      toast.success(`Date selection link sent to ${trial.parent_email}`);
      fetchTrials();
    } catch (error) {
      console.error('Failed to send date selection link:', error);
      toast.error('Failed to send date selection link');
    } finally {
      setSendingDateLink(null);
    }
  };

  const getEmailStatus = (trial) => {
    const statuses = [];
    
    statuses.push({
      type: 'confirmation',
      label: 'Confirmation',
      sent: true,
      date: trial.created_at
    });
    
    statuses.push({
      type: 'reminder',
      label: 'Reminder',
      sent: !!trial.reminder_sent_at,
      date: trial.reminder_sent_at
    });
    
    statuses.push({
      type: 'followup',
      label: 'Follow-up',
      sent: !!trial.followup_sent_at,
      date: trial.followup_sent_at
    });
    
    return statuses;
  };

  const getTrialStatus = (trial) => {
    if (trial.attendance === 'attended') {
      return { label: 'Attended', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' };
    }
    if (trial.attendance === 'no_show') {
      return { label: 'No Show', color: 'bg-red-500/20 text-red-300 border-red-500/30' };
    }
    if (trial.converted) {
      return { label: 'Converted', color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' };
    }
    
    const trialDate = new Date(trial.trial_date);
    const now = new Date();
    const diffHours = (trialDate - now) / (1000 * 60 * 60);
    
    if (trial.followup_sent_at) {
      return { label: 'Awaiting Response', color: 'bg-purple-500/20 text-purple-300 border-purple-500/30' };
    }
    if (diffHours < 0) {
      return { label: 'Needs Update', color: 'bg-orange-500/20 text-orange-300 border-orange-500/30' };
    }
    if (diffHours < 48) {
      return { label: 'Upcoming', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' };
    }
    return { label: 'Scheduled', color: 'bg-gray-500/20 text-gray-300 border-gray-500/30' };
  };

  const needsReminder = (trial) => {
    if (trial.reminder_sent_at) return false;
    const trialDate = new Date(trial.trial_date);
    const now = new Date();
    const diffHours = (trialDate - now) / (1000 * 60 * 60);
    return diffHours > 0 && diffHours <= 72;
  };

  const needsFollowup = (trial) => {
    if (trial.followup_sent_at) return false;
    if (trial.attendance === 'no_show') return false; // Don't follow up no-shows
    const trialDate = new Date(trial.trial_date);
    const now = new Date();
    return now > trialDate;
  };

  const needsAttendanceUpdate = (trial) => {
    // If already marked, no update needed
    if (trial.attendance) return false;
    
    // If no trial date, don't show automatic prompt (they need to set date first)
    if (!trial.trial_date || trial.trial_date === 'To be confirmed') return false;
    
    const trialDate = new Date(trial.trial_date);
    // Check if date is valid
    if (isNaN(trialDate.getTime())) return false;
    
    const now = new Date();
    return now > trialDate;
  };
  
  // Check if we can manually mark attendance (for admin override)
  const canMarkAttendance = (trial) => {
    return !trial.attendance; // Can mark if not already marked
  };

  const handleMarkAttendance = async (trialId, attendance) => {
    setUpdatingAttendance(trialId);
    try {
      const response = await api.patch(`/free-trials/${trialId}/attendance`, { attendance });
      if (attendance === 'attended') {
        toast.success('Marked as Attended - Follow-up email will be sent in 6 hours!', {
          duration: 5000
        });
      } else {
        toast.success('Marked as No Show');
      }
      fetchTrials();
    } catch (error) {
      console.error('Failed to update attendance:', error);
      toast.error('Failed to update attendance');
    } finally {
      setUpdatingAttendance(null);
    }
  };

  const handleDeleteTrial = async (trialId, studentName) => {
    if (!confirm(`Delete the trial sign-up for ${studentName}? It will move to Recently Deleted, where you can restore it.`)) {
      return;
    }
    
    setDeletingTrial(trialId);
    try {
      await api.delete(`/free-trials/${trialId}`);
      toast.success('Trial moved to Recently Deleted — you can restore it from there');
      fetchTrials();
      if (showDeleted) fetchDeletedTrials();
    } catch (error) {
      console.error('Failed to delete trial:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete trial');
    } finally {
      setDeletingTrial(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTrialDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Filter counts
  const countStartOfToday = new Date();
  countStartOfToday.setHours(0, 0, 0, 0);
  const counts = {
    all: trials.length,
    upcoming: trials.filter(t => new Date(t.trial_date) >= countStartOfToday).length,
    needsReminder: trials.filter(needsReminder).length,
    needsFollowup: trials.filter(needsFollowup).length,
    attended: trials.filter(t => t.attendance === 'attended').length,
    noShow: trials.filter(t => t.attendance === 'no_show').length,
    needsUpdate: trials.filter(needsAttendanceUpdate).length
  };

  // Apply filter
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const searchQ = searchTerm.trim().toLowerCase();
  const matchesSearch = (trial) => {
    if (!searchQ) return true;
    return [
      `${trial.student_first_name || ''} ${trial.student_last_name || ''}`,
      trial.parent_name,
      trial.parent_email,
      trial.parent_phone,
      trial.class_interest,
      trial.course,
    ].some((v) => (v || '').toLowerCase().includes(searchQ));
  };
  const filteredTrials = trials.filter(trial => {
    if (!matchesSearch(trial)) return false;
    switch (activeFilter) {
      case 'upcoming':
        return new Date(trial.trial_date) >= startOfToday;
      case 'needsReminder':
        return needsReminder(trial);
      case 'needsFollowup':
        return needsFollowup(trial);
      case 'attended':
        return trial.attendance === 'attended';
      case 'noShow':
        return trial.attendance === 'no_show';
      case 'needsUpdate':
        return needsAttendanceUpdate(trial);
      default:
        return true;
    }
  }).sort((a, b) => {
    // Sort by trial_date: today's + upcoming at top, past trials at bottom (newest-past first).
    // Trials stay in the upcoming section until the END of their day.
    const todayTs = startOfToday.getTime();
    const aTs = a.trial_date ? new Date(a.trial_date).getTime() : Number.POSITIVE_INFINITY;
    const bTs = b.trial_date ? new Date(b.trial_date).getTime() : Number.POSITIVE_INFINITY;
    const aUpcoming = aTs >= todayTs;
    const bUpcoming = bTs >= todayTs;
    if (aUpcoming && !bUpcoming) return -1;
    if (!aUpcoming && bUpcoming) return 1;
    if (aUpcoming && bUpcoming) return aTs - bTs; // soonest upcoming first
    return bTs - aTs; // most-recent past first
  });

  // ---- Bulk delete selection ----
  const visibleTrialIds = filteredTrials.map(t => t.id);
  const allVisibleSelected = visibleTrialIds.length > 0 && visibleTrialIds.every(id => selectedTrialIds.has(id));

  const toggleTrialSelected = (id) => {
    setSelectedTrialIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAllTrials = () => {
    setSelectedTrialIds(prev => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        visibleTrialIds.forEach(id => next.delete(id));
        return next;
      }
      return new Set([...prev, ...visibleTrialIds]);
    });
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedTrialIds];
    if (!ids.length) return;
    setBulkDeleting(true);
    try {
      await api.post('/free-trials/bulk-delete', { trial_ids: ids });
      toast.success(`${ids.length} trial booking${ids.length === 1 ? '' : 's'} moved to Recently Deleted`);
      setSelectedTrialIds(new Set());
      setBulkConfirmOpen(false);
      fetchTrials();
      if (showDeleted) fetchDeletedTrials();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete selected trials');
    } finally {
      setBulkDeleting(false);
    }
  };

  const StatCard = ({ label, count, color, icon: Icon, filterKey, isActive }) => (
    <Card 
      className={`bg-[#111] border-[#333] cursor-pointer transition-all hover:border-[#555] ${
        isActive ? 'ring-2 ring-[#FF007F] border-[#FF007F]' : ''
      }`}
      onClick={() => setActiveFilter(filterKey === activeFilter ? 'all' : filterKey)}
    >
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-white/50">{label}</p>
            <p className={`text-2xl font-bold ${color}`}>{count}</p>
          </div>
          <div className="flex items-center gap-1">
            <Icon className={`w-8 h-8 ${color} opacity-30`} />
            {isActive && <ChevronRight className="w-4 h-4 text-[#FF007F]" />}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="trials-tab">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="font-heading text-xl font-semibold text-white">Free Trial Management</h2>
          <p className="text-sm text-white/50">Track trial bookings, attendance, and email communications</p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchTrials}
          disabled={trialsLoading}
          className="border-[#333] text-white hover:bg-[#222]"
        >
          {trialsLoading ? 'Loading...' : 'Refresh'}
        </Button>
      </div>

      {/* Referral insights: monthly breakdown of "How did you hear about us?" */}
      <ReferralInsightsCard />

      {/* Stats Cards - Clickable Filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <StatCard 
          label="Total" 
          count={counts.all} 
          color="text-white" 
          icon={Users} 
          filterKey="all"
          isActive={activeFilter === 'all'}
        />
        <StatCard 
          label="Upcoming" 
          count={counts.upcoming} 
          color="text-yellow-400" 
          icon={Calendar} 
          filterKey="upcoming"
          isActive={activeFilter === 'upcoming'}
        />
        <StatCard 
          label="Need Reminder" 
          count={counts.needsReminder} 
          color="text-orange-400" 
          icon={Mail} 
          filterKey="needsReminder"
          isActive={activeFilter === 'needsReminder'}
        />
        <StatCard 
          label="Need Follow-up" 
          count={counts.needsFollowup} 
          color="text-purple-400" 
          icon={Send} 
          filterKey="needsFollowup"
          isActive={activeFilter === 'needsFollowup'}
        />
        <StatCard 
          label="Attended" 
          count={counts.attended} 
          color="text-emerald-400" 
          icon={UserCheck} 
          filterKey="attended"
          isActive={activeFilter === 'attended'}
        />
        <StatCard 
          label="No Show" 
          count={counts.noShow} 
          color="text-red-400" 
          icon={UserX} 
          filterKey="noShow"
          isActive={activeFilter === 'noShow'}
        />
        <StatCard 
          label="Update Needed" 
          count={counts.needsUpdate} 
          color="text-amber-400" 
          icon={AlertCircle} 
          filterKey="needsUpdate"
          isActive={activeFilter === 'needsUpdate'}
        />
      </div>

      {/* Active Filter Badge */}
      {activeFilter !== 'all' && (
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-[#FF007F]" />
          <span className="text-sm text-white/70">Filtering by:</span>
          <Badge className="bg-[#FF007F]/20 text-[#FF007F] border-[#FF007F]/30">
            {activeFilter === 'upcoming' && 'Upcoming Trials'}
            {activeFilter === 'needsReminder' && 'Need Reminder'}
            {activeFilter === 'needsFollowup' && 'Need Follow-up'}
            {activeFilter === 'attended' && 'Attended'}
            {activeFilter === 'noShow' && 'No Show'}
            {activeFilter === 'needsUpdate' && 'Update Needed'}
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setActiveFilter('all')}
            className="h-6 w-6 p-0 text-white/50 hover:text-white"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Trials List */}
      <Card className="bg-[#111] border-[#333]">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-[#FF007F]" />
            Trial Bookings
            <span className="text-sm font-normal text-white/50">
              ({filteredTrials.length} {filteredTrials.length === 1 ? 'trial' : 'trials'})
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {trialsLoading ? (
            <div className="p-8 text-center text-white/50">Loading trials...</div>
          ) : filteredTrials.length === 0 ? (
            <div className="p-8 text-center">
              <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/50">
                {searchQ
                  ? `No trials matching "${searchTerm}"`
                  : activeFilter !== 'all'
                    ? `No trials matching "${activeFilter}" filter`
                    : 'No free trial bookings yet'
                }
              </p>
              {searchQ && (
                <Button
                  variant="link"
                  onClick={() => setSearchTerm('')}
                  className="text-[#FF007F] mt-2"
                  data-testid="clear-search-btn"
                >
                  Clear search
                </Button>
              )}
              {!searchQ && activeFilter !== 'all' && (
                <Button
                  variant="link"
                  onClick={() => setActiveFilter('all')}
                  className="text-[#FF007F] mt-2"
                >
                  Show all trials
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Bulk selection toolbar */}
              <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-[#333] bg-[#0d0d0d]" data-testid="trials-bulk-toolbar">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllTrials}
                    className="w-5 h-5 accent-[#FF007F] cursor-pointer"
                    data-testid="select-all-trials-checkbox"
                  />
                  <span className="text-sm text-white/70">Select all ({filteredTrials.length})</span>
                </label>
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search student, parent, email, phone or class..."
                    className="pl-9 h-9 bg-[#1a1a1a] border-[#333] text-white placeholder:text-white/30"
                    data-testid="trials-search-input"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
                      data-testid="trials-search-clear"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {selectedTrialIds.size > 0 && (
                  <>
                    <Badge className="bg-[#FF007F]/20 text-[#FF007F] border-[#FF007F]/30">{selectedTrialIds.size} selected</Badge>
                    <Button
                      size="sm"
                      onClick={() => setBulkConfirmOpen(true)}
                      disabled={bulkDeleting}
                      className="bg-red-600 hover:bg-red-700 text-white ml-auto"
                      data-testid="bulk-delete-trials-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete Selected ({selectedTrialIds.size})
                    </Button>
                  </>
                )}
              </div>

              {/* No Shows Section - Show at top when filtering for no-shows or when there are recent no-shows */}
              {(activeFilter === 'noShow' || (activeFilter === 'all' && counts.noShow > 0)) && (
                <div className="mb-4">
                  <div className="bg-gradient-to-r from-red-950/50 to-red-900/30 border-l-4 border-red-500 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                          <UserX className="w-5 h-5 text-red-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-red-300 text-lg">No-Shows ({counts.noShow})</h3>
                          <p className="text-sm text-red-400/70">Students who didn't attend their trial</p>
                        </div>
                      </div>
                      {activeFilter !== 'noShow' && counts.noShow > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setActiveFilter('noShow')}
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        >
                          View All No-Shows
                        </Button>
                      )}
                    </div>
                    
                    {/* No-Show Cards */}
                    <div className="grid gap-3 mt-4">
                      {(activeFilter === 'noShow' ? filteredTrials : trials.filter(t => t.attendance === 'no_show').slice(0, 3)).map(trial => (
                        <div 
                          key={trial.id} 
                          className="bg-[#111] rounded-lg border border-red-500/30 p-4 hover:border-red-500/50 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <input
                              type="checkbox"
                              checked={selectedTrialIds.has(trial.id)}
                              onChange={() => toggleTrialSelected(trial.id)}
                              className="w-5 h-5 mt-1 accent-[#FF007F] cursor-pointer shrink-0"
                              data-testid={`select-noshow-${trial.id}`}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="font-semibold text-white">
                                  {trial.student_first_name} {trial.student_last_name}
                                </span>
                                <Badge className="bg-red-500/20 text-red-300 border-red-500/30 text-xs">
                                  No Show
                                </Badge>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-sm text-white/60">
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  <span>{formatTrialDate(trial.trial_date)}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  <span>{trial.parent_name}</span>
                                </div>
                              </div>
                              {trial.parent_email && (
                                <div className="flex items-center gap-1 text-sm text-white/60 mt-1">
                                  <Mail className="w-3 h-3" />
                                  <span>{trial.parent_email}</span>
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  const baseUrl = window.location.origin;
                                  const trialLink = `${baseUrl}/free-trial`;
                                  const subject = encodeURIComponent(`Reschedule ${trial.student_first_name}'s Free Trial - UPA`);
                                  const body = encodeURIComponent(
`Hi ${trial.parent_name},

We noticed ${trial.student_first_name} wasn't able to make it to their scheduled trial on ${new Date(trial.trial_date).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}. We'd love to give them another chance to experience our classes!

You can easily book a new trial date here:
${trialLink}

If you have any questions or need help finding a suitable time, just reply to this email or give us a call.

We look forward to seeing ${trial.student_first_name} soon!

Best regards,
The UPA Team`
                                  );
                                  window.location.href = `mailto:${trial.parent_email}?subject=${subject}&body=${body}`;
                                }}
                                className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 text-xs"
                                title="Send reschedule email"
                                data-testid={`reschedule-noshow-${trial.id}`}
                              >
                                <Mail className="w-3 h-3 mr-1" />
                                Reschedule
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.location.href = `tel:${trial.parent_phone}`}
                                className="border-green-500/30 text-green-400 hover:bg-green-500/10 text-xs"
                                title="Call parent"
                              >
                                <Phone className="w-3 h-3 mr-1" />
                                Call
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleMarkAttendance(trial.id, 'attended')}
                                disabled={updatingAttendance === trial.id}
                                className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 text-xs"
                                title="Mark as attended (if error)"
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Fix
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteTrial(trial.id, `${trial.student_first_name} ${trial.student_last_name}`)}
                                disabled={deletingTrial === trial.id}
                                className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs"
                                title="Delete this no-show record"
                                data-testid={`delete-noshow-${trial.id}`}
                              >
                                {deletingTrial === trial.id ? (
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                  <Trash2 className="w-3 h-3 mr-1" />
                                )}
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {activeFilter !== 'noShow' && counts.noShow > 3 && (
                      <div className="mt-3 text-center">
                        <Button
                          variant="link"
                          onClick={() => setActiveFilter('noShow')}
                          className="text-red-400 hover:text-red-300"
                        >
                          View all {counts.noShow} no-shows →
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Regular Trials List (exclude no-shows when showing no-show section and not filtering) */}
              <div className="divide-y divide-[#333]">
                {(activeFilter === 'noShow' ? [] : filteredTrials.filter(t => activeFilter !== 'all' || t.attendance !== 'no_show')).map(trial => {
                  const status = getTrialStatus(trial);
                  const emailStatuses = getEmailStatus(trial);
                  const showAttendanceButtons = needsAttendanceUpdate(trial);
                  
                  return (
                    <div key={trial.id} className={`p-4 hover:bg-[#1a1a1a] transition-colors ${selectedTrialIds.has(trial.id) ? 'bg-[#FF007F]/5' : ''}`}>
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selectedTrialIds.has(trial.id)}
                          onChange={() => toggleTrialSelected(trial.id)}
                          className="w-5 h-5 mt-1.5 accent-[#FF007F] cursor-pointer shrink-0"
                          data-testid={`select-trial-${trial.id}`}
                        />
                        <div className="flex-1 min-w-0">
                      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                        {/* Person Details */}
                        <div className="flex-1 space-y-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-white text-lg">
                              {trial.student_first_name} {trial.student_last_name}
                            </h3>
                            <Badge className={status.color}>{status.label}</Badge>
                            {trial.contact_method === 'whatsapp' && (
                              <Badge className="bg-[#25D366]/20 text-[#25D366] border-[#25D366]/40 flex items-center gap-1" data-testid={`whatsapp-contacted-badge-${trial.id}`}>
                                <MessageCircle className="w-3 h-3" />
                                Contacted via WhatsApp
                              </Badge>
                            )}
                          </div>
                          
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                            <div className="flex items-center gap-2 text-white/70">
                              <Baby className="w-4 h-4 text-[#FF007F]" />
                              <span>Age: {trial.student_age || 'N/A'}</span>
                              {trial.student_dob && (
                                <span className="text-white/50">
                                  (DOB: {new Date(trial.student_dob).toLocaleDateString('en-GB')})
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-white/70">
                              <User className="w-4 h-4 text-[#7000FF]" />
                              <span>Parent: {trial.parent_name}</span>
                            </div>
                            <div className="flex items-center gap-2 text-white/70">
                              <Mail className="w-4 h-4 text-blue-400" />
                              <a href={`mailto:${trial.parent_email}`} className="hover:text-blue-400">
                                {trial.parent_email}
                              </a>
                            </div>
                            <div className="flex items-center gap-2 text-white/70">
                              <Phone className="w-4 h-4 text-green-400" />
                              <a href={`tel:${trial.parent_phone}`} className="hover:text-green-400">
                                {trial.parent_phone}
                              </a>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 text-white/70 bg-[#1a1a1a] rounded-lg p-2 mt-2">
                            <Calendar className="w-4 h-4 text-yellow-400" />
                            <span className="font-medium">Trial Date:</span>
                            <span className="text-yellow-400">{formatTrialDate(trial.trial_date)}</span>
                          </div>

                          {trial.class_interest && (
                            <p className="text-sm text-white/50">
                              Interested in: <span className="text-[#FF007F]">{trial.class_interest}</span>
                            </p>
                          )}

                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setWaTrial(trial)}
                            className="border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10 text-xs mt-1"
                            data-testid={`whatsapp-msg-btn-${trial.id}`}
                          >
                            <MessageCircle className="w-3 h-3 mr-1" />
                            WhatsApp Message
                          </Button>

                          {/* Attendance Buttons */}
                          {showAttendanceButtons && (
                            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mt-3">
                              <p className="text-sm text-amber-300 mb-2 flex items-center gap-2">
                                <AlertCircle className="w-4 h-4" />
                                Did this student attend their trial?
                              </p>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleMarkAttendance(trial.id, 'attended')}
                                  disabled={updatingAttendance === trial.id}
                                  className="bg-emerald-600 hover:bg-emerald-700"
                                >
                                  {updatingAttendance === trial.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <UserCheck className="w-4 h-4 mr-1" />
                                      Yes, Attended
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleMarkAttendance(trial.id, 'no_show')}
                                  disabled={updatingAttendance === trial.id}
                                  variant="outline"
                                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                                >
                                  {updatingAttendance === trial.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <UserX className="w-4 h-4 mr-1" />
                                      No Show
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          )}

                        {/* Show attendance status if already marked */}
                        {trial.attendance && (
                          <div className={`rounded-lg p-3 mt-3 ${
                            trial.attendance === 'attended' 
                              ? 'bg-emerald-500/10 border border-emerald-500/30' 
                              : 'bg-red-500/10 border border-red-500/30'
                          }`}>
                            <div className="flex items-center gap-2">
                              {trial.attendance === 'attended' ? (
                                <>
                                  <UserCheck className="w-5 h-5 text-emerald-400" />
                                  <span className="text-emerald-300 font-medium">Student attended the trial</span>
                                </>
                              ) : (
                                <>
                                  <UserX className="w-5 h-5 text-red-400" />
                                  <span className="text-red-300 font-medium">Student did not attend (No Show)</span>
                                </>
                              )}
                            </div>
                            {trial.attendance === 'attended' && trial.auto_followup_scheduled_at && !trial.auto_followup_sent && (
                              <div className="flex items-center gap-2 mt-2 text-sm text-emerald-400/70">
                                <Clock className="w-4 h-4" />
                                <span>Auto follow-up scheduled for {formatDate(trial.auto_followup_scheduled_at)}</span>
                              </div>
                            )}
                            {trial.auto_followup_sent && (
                              <div className="flex items-center gap-2 mt-2 text-sm text-emerald-400">
                                <CheckCircle className="w-4 h-4" />
                                <span>Auto follow-up sent {trial.auto_followup_sent_at ? formatDate(trial.auto_followup_sent_at) : ''}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Email Tracking */}
                      <div className="lg:w-80 space-y-3">
                        <h4 className="text-sm font-semibold text-white/70 flex items-center gap-2">
                          <Mail className="w-4 h-4" /> Email Journey
                        </h4>
                        <div className="space-y-2">
                          {emailStatuses.map((email, idx) => (
                            <div 
                              key={email.type}
                              className={`flex items-center justify-between p-2 rounded-lg border ${
                                email.sent 
                                  ? 'bg-emerald-500/10 border-emerald-500/30' 
                                  : 'bg-[#1a1a1a] border-[#333]'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                {email.sent ? (
                                  <MailCheck className="w-4 h-4 text-emerald-400" />
                                ) : (
                                  <MailX className="w-4 h-4 text-white/30" />
                                )}
                                <span className={email.sent ? 'text-emerald-300' : 'text-white/50'}>
                                  {email.label}
                                </span>
                              </div>
                              {email.sent ? (
                                <span className="text-xs text-emerald-400/70">
                                  {formatDate(email.date)}
                                </span>
                              ) : (
                                <span className="text-xs text-white/30">Not sent</span>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap gap-2 pt-2">
                          {/* View Details */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setDetailsTrial(trial)}
                            className="border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10"
                            title="View all trial info"
                            data-testid={`view-trial-details-${trial.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Details
                          </Button>

                          {/* Import as Student (shortcut — prefills Add Students form) */}
                          <Button
                            size="sm"
                            onClick={() => handleImportFromTrial(trial)}
                            className="bg-[#FF007F] hover:bg-[#D6006A]"
                            title="Open Add Students form with trial data pre-filled"
                            data-testid={`import-from-trial-${trial.id}`}
                          >
                            <UserPlus className="w-4 h-4 mr-1" />
                            Import as Student
                          </Button>

                          {/* Edit Trial Date */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditTrialDate(trial)}
                            className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                            title="Edit trial date"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Edit Date
                          </Button>
                          
                          {/* Send Date Selection Link (if no date set) */}
                          {(!trial.trial_date || trial.trial_date === 'To be confirmed') && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleSendDateSelectionLink(trial)}
                              disabled={sendingDateLink === trial.id}
                              className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                              title="Send link to choose date"
                            >
                              {sendingDateLink === trial.id ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                              ) : (
                                <Link2 className="w-4 h-4 mr-1" />
                              )}
                              Send Date Link
                            </Button>
                          )}
                          
                          {/* Manual Attendance Buttons - always show if not marked */}
                          {!trial.attendance && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleMarkAttendance(trial.id, 'attended')}
                                disabled={updatingAttendance === trial.id}
                                className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                                title="Mark as attended"
                              >
                                <UserCheck className="w-4 h-4 mr-1" />
                                Attended
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleMarkAttendance(trial.id, 'no_show')}
                                disabled={updatingAttendance === trial.id}
                                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                                title="Mark as no show"
                              >
                                <UserX className="w-4 h-4 mr-1" />
                                No Show
                              </Button>
                            </>
                          )}
                          
                          {needsReminder(trial) && (
                            <Button
                              size="sm"
                              onClick={() => handleSendTrialReminder(trial.id)}
                              disabled={sendingTrialEmail === `reminder-${trial.id}`}
                              className="flex-1 bg-orange-500 hover:bg-orange-600"
                            >
                              {sendingTrialEmail === `reminder-${trial.id}` ? (
                                'Sending...'
                              ) : (
                                <>
                                  <Clock className="w-4 h-4 mr-1" />
                                  Send Reminder
                                </>
                              )}
                            </Button>
                          )}
                          {needsFollowup(trial) && trial.attendance === 'attended' && (
                            <Button
                              size="sm"
                              onClick={() => handleSendTrialFollowup(trial.id)}
                              disabled={sendingTrialEmail === `followup-${trial.id}`}
                              className="flex-1 bg-purple-500 hover:bg-purple-600"
                            >
                              {sendingTrialEmail === `followup-${trial.id}` ? (
                                'Sending...'
                              ) : (
                                <>
                                  <Send className="w-4 h-4 mr-1" />
                                  Send Follow-up
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Submission timestamp and delete button */}
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-xs text-white/30">
                        Submitted: {formatDate(trial.created_at)}
                      </p>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteTrial(trial.id, `${trial.student_first_name} ${trial.student_last_name}`)}
                        disabled={deletingTrial === trial.id}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid={`delete-trial-${trial.id}`}
                      >
                        {deletingTrial === trial.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4 mr-1" />
                            Delete
                          </>
                        )}
                      </Button>
                    </div>
                        </div>
                      </div>
                  </div>
                );
              })}
            </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Recently Deleted (soft-deleted trials, restorable) */}
      <Card className="bg-[#111] border-[#333]">
        <CardHeader
          className="cursor-pointer select-none"
          onClick={() => setShowDeleted(!showDeleted)}
          data-testid="recently-deleted-toggle"
        >
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <Trash2 className="w-4 h-4 text-red-400" />
            Recently Deleted
            {deletedTrials.length > 0 && showDeleted && (
              <span className="text-sm font-normal text-white/50">({deletedTrials.length})</span>
            )}
            <ChevronRight className={`w-4 h-4 text-white/50 transition-transform ${showDeleted ? 'rotate-90' : ''}`} />
          </CardTitle>
        </CardHeader>
        {showDeleted && (
          <CardContent>
            {deletedLoading ? (
              <p className="text-white/50 text-sm">Loading...</p>
            ) : deletedTrials.length === 0 ? (
              <p className="text-white/50 text-sm">
                No deleted trials. Anything you delete from now on will appear here and can be restored.
              </p>
            ) : (
              <div className="space-y-2">
                {deletedTrials.map(t => (
                  <div
                    key={t.id}
                    className="flex flex-wrap items-center justify-between gap-2 p-3 bg-[#0a0a0a] rounded-lg border border-[#222]"
                    data-testid={`deleted-trial-${t.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-white font-medium">{t.student_first_name} {t.student_last_name}</p>
                      <p className="text-xs text-white/50 truncate">
                        {t.parent_name}{t.parent_email ? ` · ${t.parent_email}` : ''}{t.parent_phone ? ` · ${t.parent_phone}` : ''}{t.class_interest ? ` · ${t.class_interest}` : ''}
                      </p>
                      <p className="text-xs text-white/40">
                        Deleted {formatDate(t.deleted_at)}{t.deleted_by_name ? ` by ${t.deleted_by_name}` : ''}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleRestoreTrial(t.id, `${t.student_first_name || ''} ${t.student_last_name || ''}`.trim() || 'Trial')}
                      disabled={restoringTrial === t.id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid={`restore-trial-${t.id}`}
                    >
                      {restoringTrial === t.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <><Undo2 className="w-4 h-4 mr-1" />Restore</>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-400" />
              Delete {selectedTrialIds.size} trial booking{selectedTrialIds.size === 1 ? '' : 's'}?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-white/70">
            The selected trial bookings will move to <strong>Recently Deleted</strong>, where they can be restored at any time.
            All other trial records will remain unaffected.
          </p>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setBulkConfirmOpen(false)}
              disabled={bulkDeleting}
              className="border-[#333] text-white hover:bg-[#222]"
              data-testid="bulk-delete-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="bulk-delete-confirm-btn"
            >
              {bulkDeleting ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Deleting...</>
              ) : (
                <>Delete {selectedTrialIds.size}</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Trial Details Dialog */}
      <Dialog open={!!detailsTrial} onOpenChange={() => setDetailsTrial(null)}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-indigo-400" />
              Trial Details — {detailsTrial?.student_first_name} {detailsTrial?.student_last_name}
            </DialogTitle>
          </DialogHeader>
          {detailsTrial && (
            <div className="space-y-4 py-2">
              <div className="bg-[#0a0a0a] border border-[#222] rounded-lg p-4 space-y-2">
                <h4 className="text-sm font-semibold text-indigo-300 uppercase tracking-wide mb-2">Student</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-white/50">First name:</span> <span className="text-white">{detailsTrial.student_first_name || '—'}</span></div>
                  <div><span className="text-white/50">Middle name:</span> <span className="text-white">{detailsTrial.student_middle_name || '—'}</span></div>
                  <div><span className="text-white/50">Last name:</span> <span className="text-white">{detailsTrial.student_last_name || '—'}</span></div>
                  <div><span className="text-white/50">Date of birth:</span> <span className="text-white">{detailsTrial.student_dob || '—'}</span></div>
                  <div><span className="text-white/50">Age:</span> <span className="text-white">{detailsTrial.student_age || '—'}</span></div>
                  <div><span className="text-white/50">Gender:</span> <span className="text-white">{detailsTrial.student_gender || '—'}</span></div>
                </div>
                <div className="text-sm pt-2 border-t border-[#222]">
                  <span className="text-white/50">Medical conditions / allergies:</span>
                  <div className="mt-1 text-white whitespace-pre-wrap">{detailsTrial.medical_conditions || 'None reported'}</div>
                </div>
              </div>

              <div className="bg-[#0a0a0a] border border-[#222] rounded-lg p-4 space-y-2">
                <h4 className="text-sm font-semibold text-indigo-300 uppercase tracking-wide mb-2">Guardian 1</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-white/50">Name:</span> <span className="text-white">{detailsTrial.parent_name || '—'}</span></div>
                  <div><span className="text-white/50">Email:</span> <a href={`mailto:${detailsTrial.parent_email}`} className="text-blue-400 hover:underline">{detailsTrial.parent_email || '—'}</a></div>
                  <div><span className="text-white/50">Phone:</span> <a href={`tel:${detailsTrial.parent_phone}`} className="text-green-400 hover:underline">{detailsTrial.parent_phone || '—'}</a></div>
                </div>
              </div>

              {detailsTrial.guardian2 && detailsTrial.guardian2.first_name && (
                <div className="bg-[#0a0a0a] border border-[#222] rounded-lg p-4 space-y-2">
                  <h4 className="text-sm font-semibold text-indigo-300 uppercase tracking-wide mb-2">Guardian 2</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-white/50">Name:</span> <span className="text-white">{detailsTrial.guardian2.first_name} {detailsTrial.guardian2.last_name || ''}</span></div>
                    <div><span className="text-white/50">Email:</span> <span className="text-white">{detailsTrial.guardian2.email || '—'}</span></div>
                    <div><span className="text-white/50">Phone:</span> <span className="text-white">{detailsTrial.guardian2.phone || '—'}</span></div>
                  </div>
                </div>
              )}

              {detailsTrial.address && detailsTrial.address.street && (
                <div className="bg-[#0a0a0a] border border-[#222] rounded-lg p-4 space-y-1 text-sm">
                  <h4 className="text-sm font-semibold text-indigo-300 uppercase tracking-wide mb-2">Address</h4>
                  <div className="text-white">{detailsTrial.address.street}</div>
                  {detailsTrial.address.street2 && <div className="text-white">{detailsTrial.address.street2}</div>}
                  <div className="text-white">{[detailsTrial.address.city, detailsTrial.address.postcode].filter(Boolean).join(' ')}</div>
                  {detailsTrial.address.country && <div className="text-white/70">{detailsTrial.address.country}</div>}
                </div>
              )}

              <div className="bg-[#0a0a0a] border border-[#222] rounded-lg p-4 space-y-1 text-sm">
                <h4 className="text-sm font-semibold text-indigo-300 uppercase tracking-wide mb-2">Trial</h4>
                <div><span className="text-white/50">Class interest:</span> <span className="text-[#FF007F]">{detailsTrial.class_interest || '—'}</span></div>
                <div><span className="text-white/50">Trial date:</span> <span className="text-yellow-400">{detailsTrial.trial_date || '—'}</span></div>
                <div><span className="text-white/50">Status:</span> <span className="text-white">{detailsTrial.attendance || detailsTrial.status || '—'}</span></div>
                <div><span className="text-white/50">Submitted:</span> <span className="text-white/70">{detailsTrial.created_at || '—'}</span></div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => handleCopyTrialDetails(detailsTrial)}
              className="border-[#333] text-white hover:bg-white/10"
              data-testid="copy-trial-details"
            >
              <CopyIcon className="w-4 h-4 mr-2" />
              Copy details
            </Button>
            <Button
              onClick={() => {
                const t = detailsTrial;
                setDetailsTrial(null);
                handleImportFromTrial(t);
              }}
              className="bg-[#FF007F] hover:bg-[#D6006A]"
              data-testid="import-from-trial-modal"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Import as Student
            </Button>
            <Button
              variant="outline"
              onClick={() => setDetailsTrial(null)}
              className="border-[#333] text-white hover:bg-white/10"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Trial Date Dialog */}
      <Dialog open={!!editingTrial} onOpenChange={() => setEditingTrial(null)}>
        <DialogContent className="bg-[#111] border-[#333] text-white">
          <DialogHeader>
            <DialogTitle className="text-white">
              Edit Trial Date for {editingTrial?.student_first_name} {editingTrial?.student_last_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-white/70">Select New Trial Date</Label>
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-[#0a0a0a] border-[#333] text-white"
                data-testid="edit-trial-date-input"
              />
              <p className="text-xs text-white/40">
                You can pick any date — today, future, or past (e.g. to correct a historical record).
              </p>
            </div>
            {editingTrial?.trial_date && editingTrial.trial_date !== 'To be confirmed' && (
              <p className="text-sm text-white/50">
                Current date: {formatTrialDate(editingTrial.trial_date)}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setEditingTrial(null)}
              className="border-[#333] text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveTrialDate}
              disabled={savingDate || !selectedDate}
              className="bg-[#FF007F] hover:bg-[#D6006A]"
            >
              {savingDate ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                'Save Date'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TrialWhatsAppDialog trial={waTrial} onClose={() => setWaTrial(null)} />
    </div>
  );
};

export default TrialsTab;
